import React, { useState, useEffect } from "react"
import { X, ZoomIn, Download, RotateCw } from "lucide-react"
import { twMerge } from "tailwind-merge"

interface ImagePreviewProps {
  src: string // Supports both base64 (data:image/...) and URL images
  alt?: string
  className?: string
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "free"
  rounded?: boolean
  showFullscreenButton?: boolean
  downloadable?: boolean
  initIsFullScreen?: boolean
  onClose?: () => void
}

const ImagePreview: React.FC<ImagePreviewProps> = ({
  src,
  alt = "Image preview",
  className = "",
  size = "md",
  rounded = false,
  showFullscreenButton = true,
  downloadable = false,
  initIsFullScreen,
  onClose,
}) => {
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(initIsFullScreen ?? false)
  const [imageError, setImageError] = useState(false)
  const [rotation, setRotation] = useState(0)

  // Handle ESC key to close fullscreen
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isFullscreenOpen) {
        setIsFullscreenOpen(false)
        onClose?.()
      }
    }

    if (isFullscreenOpen) {
      document.addEventListener("keydown", handleKeyDown)
      return () => document.removeEventListener("keydown", handleKeyDown)
    }
  }, [isFullscreenOpen])

  // Size configurations
  const sizeClasses = {
    xs: "min-w-8 min-h-8 w-8 h-8",
    sm: "min-w-12 min-h-12 w-12 h-12",
    md: "min-w-16 min-h-16 w-16 h-16",
    lg: "min-w-24 min-h-24 w-24 h-24",
    xl: "min-w-32 min-h-32 w-32 h-32",
    free: "min-w-full min-h-full w-full h-full",
  }

  // const fullscreenSizeClasses = {
  //   sm: "max-w-sm max-h-sm",
  //   md: "max-w-md max-h-md",
  //   lg: "max-w-lg max-h-lg",
  //   xl: "max-w-xl max-h-xl",
  // }

  const handleImageClick = () => {
    if (showFullscreenButton && !imageError) {
      setIsFullscreenOpen(true)
    }
  }

  const handleDownload = async () => {
    try {
      // Create a temporary link element
      const link = document.createElement("a")
      link.href = src

      // If it's a base64 image, we need to handle it differently
      if (src.startsWith("data:")) {
        link.download = `legacy-profile-${Date.now()}.png`
      } else {
        link.download = src.split("/").pop() || "legacy-image"
      }

      // Trigger download
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error("Error downloading image:", error)
    }
  }

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360)
  }

  const handleImageError = () => {
    setImageError(true)
  }

  const handleImageLoad = () => {
    setImageError(false)
  }

  const onCloseHandler = () => {
    setIsFullscreenOpen(false)
    onClose?.()
  }

  if (imageError) {
    return (
      <div
        className={`${sizeClasses[size]} ${rounded ? "rounded-full" : ""} bg-legacy-light flex items-center justify-center ${className}`}
      >
        <div className="text-center">
          <div className="text-legacy-dark text-xs">Image not available</div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className={`relative ${className}`}>
        <img
          src={src}
          alt={alt}
          className={twMerge(
            sizeClasses[size],
            rounded ? "rounded-full" : "",
            "object-cover border-2 border-legacy-light",
            showFullscreenButton
              ? "cursor-pointer hover:opacity-90 hover:scale-105 transition-all duration-200"
              : "",
            initIsFullScreen && "hidden",
          )}
          onClick={handleImageClick}
          onError={handleImageError}
          onLoad={handleImageLoad}
          title={showFullscreenButton ? "Click to view fullscreen" : ""}
        />
        {showFullscreenButton && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-200 bg-black/30 rounded-lg group pointer-events-none">
            <div className="flex flex-col items-center space-y-1">
              <ZoomIn className="h-6 w-6 text-white" />
              <span className="text-xs text-white font-medium">Click to expand</span>
            </div>
          </div>
        )}
      </div>
      {isFullscreenOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
          onClick={onCloseHandler}
        >
          <div
            className="relative max-w-4xl max-h-full w-full h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onCloseHandler}
              className="absolute top-4 right-4 p-3 bg-black bg-opacity-60 hover:bg-opacity-80 rounded-full transition-all duration-200 z-10 shadow-lg hover:shadow-xl hover:scale-110"
              type="button"
            >
              <X className="h-6 w-6 text-white" />
            </button>
            <img
              src={src}
              alt={alt}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl transform transition-transform duration-300"
              style={{ transform: `rotate(${rotation}deg)` }}
              onError={handleImageError}
            />
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex flex-col items-center space-y-2 z-10">
              <div className="flex space-x-4">
                {downloadable && (
                  <button
                    onClick={handleDownload}
                    className="p-3 bg-black bg-opacity-60 hover:bg-opacity-80 rounded-full transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-110"
                    title="Download image"
                    type="button"
                  >
                    <Download className="h-5 w-5 text-white" />
                  </button>
                )}
                <button
                  onClick={handleRotate}
                  className="p-3 bg-black bg-opacity-60 hover:bg-opacity-80 rounded-full transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-110"
                  title="Rotate image"
                  type="button"
                >
                  <RotateCw className="h-5 w-5 text-white" />
                </button>
              </div>
              <div className="text-xs text-white bg-black bg-opacity-50 px-3 py-1 rounded-full">
                Press ESC or click outside to close
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ImagePreview
